/**
 * Deep Atlas. Core tokens from 07-design-mockup-build-notes.md
 * section 1 (column B); type hues from the critic's replacement map, column B;
 * `spell` from amendments 2.1 section 7. One change from those values, logged
 * in docs/reports/p0-9.md: light `accent` #755411 → #755613 (ΔE to `quest`
 * 4.69 → 5.0+ in normal vision).
 */
import { PALETTE } from '../palette.js';
export const atlas = {
    id: 'atlas',
    name: 'Deep Atlas',
    mood: "A cartographer's night study: ink-blue surfaces, brass instruments and ruled lines. A serious tool that happens to be fantasy-themed.",
    modes: {
        dark: {
            core: {
                bg: '#12161C',
                surface: '#1B212A',
                surfaceRaised: '#242C37',
                border: '#2E3846',
                borderStrong: '#3D4856',
                controlBorder: '#92A2B5',
                text: '#E6EAF0',
                textMuted: '#92A2B5',
                accent: '#C9A15A',
                accentContrast: '#12161C',
                danger: '#EE8579',
                success: '#62C8BC',
                ai: '#C9A15A',
            },
            types: {
                pc: '#57AEFF',
                npc: '#57FAD4',
                creature: '#EFBCA9',
                encounter: '#FA9A87',
                location: '#B5EE98',
                faction: '#B3BCFD',
                item: '#C7B637',
                lore: '#BCA0D4',
                arc: '#D797A7',
                quest: '#F9D95E',
                script: '#DEAEC8',
                session: '#A3E2FF',
                spell: '#AACDEA',
                note: '#92A2B5',
                deity: '#92A2B5',
            },
            palette: PALETTE.dark,
            elevation: { shadow: 'none', border: 'border' },
        },
        light: {
            core: {
                bg: '#F4F1E8',
                surface: '#FAF7EE',
                surfaceRaised: '#FFFFFF',
                border: '#D8D2C0',
                borderStrong: '#CBC5B3',
                controlBorder: '#5B6472',
                text: '#1E2530',
                textMuted: '#5B6472',
                accent: '#755613',
                accentContrast: '#FFFFFF',
                danger: '#A92B2B',
                success: '#1D695C',
                ai: '#755613',
            },
            types: {
                pc: '#2C5095',
                npc: '#08514F',
                creature: '#805443',
                encounter: '#7A0611',
                location: '#21482D',
                faction: '#4C4C73',
                item: '#313A07',
                lore: '#59247C',
                arc: '#8A1E43',
                quest: '#674709',
                script: '#4C273D',
                session: '#2E6678',
                spell: '#023558',
                note: '#5B6472',
                deity: '#5B6472',
            },
            palette: PALETTE.light,
            elevation: {
                shadow: '0 1px 2px rgb(30 37 48 / 0.08), 0 6px 16px rgb(30 37 48 / 0.1)',
                border: 'borderStrong',
            },
        },
    },
    // Deity keeps the neutral note treatment here: a pale silver-white hue does not fit
    // this direction's published color-distance floors (docs/reports/p0-9.md, Patch 1).
    userTypeHues: [],
    typography: {
        display: {
            family: '"Fraunces", "Iowan Old Style", "Palatino Linotype", Georgia, serif',
            weight: 560,
            variation: '"WONK" 0, "SOFT" 0',
        },
        heading: {
            family: '"Fraunces", "Iowan Old Style", "Palatino Linotype", Georgia, serif',
            weight: 560,
            variation: '"WONK" 0, "SOFT" 0',
        },
        body: {
            family: '"Spectral", "Iowan Old Style", "Palatino Linotype", Georgia, serif',
            weight: 400,
            variation: 'normal',
        },
        label: {
            family: '"IBM Plex Sans", "Segoe UI", system-ui, sans-serif',
            weight: 600,
            variation: 'normal',
        },
        ui: {
            family: '"IBM Plex Sans", "Segoe UI", system-ui, sans-serif',
            weight: 400,
            variation: 'normal',
        },
        mono: {
            family: '"IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace',
            weight: 400,
            variation: 'normal',
        },
        size: { title: 38, heading: 22, body: 17, ui: 14, label: 12 },
        bodyWeightDark: 400,
        labelCase: 'uppercase',
        labelTracking: '0.06em',
    },
    radii: { chip: 2, card: 2, control: 4 },
    ornament: {
        dropCapInRead: false,
        titleRule: false,
        scaleBar: true,
        divider: 'tick',
        glow: false,
        statBlockEdge: 'accent-double',
        secretBorder: 'borderStrong',
        gmNotePanel: false,
    },
    fixes: [
        'Fraunces with "WONK" 0 at weight 560 (the wonk variant is off)',
        'No folded-corner secret panel: a solid border-strong frame with a ticked header rule',
        'GM notes inline in the column, not in the margin',
        'Light accent nudged from #755411 to #755613 so it sits at least ΔE 5.0 from quest',
    ],
};
//# sourceMappingURL=atlas.js.map